import { UserPlatform } from "@interfaces/IUser";
import { CurrentUserService } from "@services/currentUser.service";
import { ILoginResponse, LoginService } from "@services/login.service.ajs";
import { NavigationService } from "@services/navigation.service";
import { PreferencesService } from "@services/preferences.service";
import { SessionService } from "@services/session.service";
import { TranslationService } from "@services/translation.service";
import * as angular from "angular";
import "./loginBox.component.scss";

enum LoginScreenPage {
    Login,
    ForgotPassword,
    AccountPicker,
    SamlLogin
}

class ComponentController implements angular.IComponentController {

    username: string;
    password: string;

    loggingIn = false;
    inError = false;
    errorMessage = "";

    screenToShow: LoginScreenPage = LoginScreenPage.Login;

    sentEmail = false;
    sendingEmail = false;

    isIE11 = false;

    loading = false;
    showPasswordBox = false;

    static $inject = ["$window", "$location", "$timeout", "loginService", "sessionService", "currentUserService", "navigationService", "translationService", "preferencesService"];

    constructor(
        private readonly $window: angular.IWindowService,
        private readonly $location: angular.ILocationService,
        private readonly $timeout: angular.ITimeoutService,
        private readonly loginService: LoginService,
        private readonly sessionService: SessionService,
        private readonly currentUserService: CurrentUserService,
        private readonly navigationService: NavigationService,
        private readonly translationService: TranslationService,
        private readonly preferencesService: PreferencesService
    ) {
    }

    $onInit(): void {
        this.checkAutomaticLogin();

        const samlFailure = this.$location.search()["samlFailure"];
        if (samlFailure) {
            this.inError = true;
            this.errorMessage = this.translationService.t("Login.SSOValidationFailure");
        }

        // This helps users who have bookmarked the login page, by going back to the login page they will be asked to log in again
        // This makes them feel like they have been logged out even if they haven't
        if (this.sessionService.hasActiveSession()) {
            this.navigationService.goToDefaultPage();
        }

        this.isIE11 = /MSIE|Trident|Edge/.test(window.navigator.userAgent) || !!window["MSInputMethodContext"] && !!document["documentMode"];
    }

    login(): void {
        this.resetStates();

        this.loggingIn = true;

        if (!this.$window.navigator.onLine) {
            this.inError = true;
            this.errorMessage = this.translationService.t("Login.NoInternetConnectionAvailable");
            return;
        }

        if (this.screenToShow == LoginScreenPage.SamlLogin) {
            this.loginSaml();
            return;
        }

        this.loginService.isSSOEnabled(this.username)
            .then(response => {
                if (!response.IsSuccess) {
                    throw new Error(response.ExceptionMessage);
                }

                this.loginService.loginSaml(this.username, true);
            })
            .catch(() => {
                this.loggingIn = false;
                this.performLogin();
            });
    }

    loginSaml(): void {
        this.resetStates();

        this.loggingIn = true;

        this.performSamlLogin();
    }

    showPasswordLogin(): void {
        this.resetStates();
        this.screenToShow = LoginScreenPage.Login;
    }

    showSamlLogin(): void {
        this.resetStates();
        this.screenToShow = LoginScreenPage.SamlLogin;
    }

    showForgottenPasswordClicked(): void {
        this.resetStates();
        this.screenToShow = LoginScreenPage.ForgotPassword;
    }

    showLoginClicked(resetStates = true): void {
        if (resetStates) {
            this.resetStates();
        }

        this.screenToShow = LoginScreenPage.Login;
    }

    sendForgotPasswordEmail(): void {
        const email = this.username;
        this.sendingEmail = true;

        this.loginService.sendForgotPasswordEmail(email).then(() => {
            this.sendingEmail = false;
            this.sentEmail = true;

            this.showLoginClicked(false);
        });
    }

    onPasswordChanged($event: string): void {
        this.password = $event;
    }

    // ---

    private showAccountPickerScreen(): void {
        this.screenToShow = LoginScreenPage.AccountPicker;
    }

    private loginViaToken(flushExsistingSession = false, loginToken: ILoginResponse): angular.IPromise<ILoginResponse | void> {
        this.loggingIn = true;

        if (flushExsistingSession) {
            // Flush any existing session
            this.sessionService.clearAllCachesAndSessions();
        }

        this.sessionService.setStorage(loginToken);

        return this.getUserInformation();
    }

    private loginViaCode(loginCode: string): void {
        this.username = "---";
        this.password = loginCode;

        this.performLogin(true);
    }

    private performLogin(flushExsistingSession = false): angular.IPromise<ILoginResponse | void> {

        this.loggingIn = true;

        return this.loginService
            .login(this.username, this.password, flushExsistingSession)
            .then(loginResponse => {
                if (!loginResponse) {
                    return;
                }

                return this.getUserInformation();
            })
            .catch(() => {
                this.inError = true;
                this.loggingIn = false;
                this.errorMessage = this.translationService.t("Login.IncorrectEmailOrPassword");
            });
    }

    private performSamlLogin(flushExsistingSession = false): void {

        this.loggingIn = true;

        this.loginService
            .isSSOEnabled(this.username)
            .then(response => {
                if (!response.IsSuccess) {
                    throw new Error(response.ExceptionMessage);
                }

                this.loginService.loginSaml(this.username, flushExsistingSession);
            })
            .catch(() => {
                this.inError = true;
                this.loggingIn = false;
                this.errorMessage = this.translationService.t("Login.NoOrganisationMatches");
            });
    }

    private getUserInformation(): angular.IPromise<void> {
        return this.currentUserService.getCurrentUser().then(currentUser => {
            if (currentUser.platform == UserPlatform.Foundation) {
                this.loginService.getAllUserAndAccountSettings().then((accountData) => {
                    this.preferencesService.reconcileLanguagesRequiresRefresh().then((refreshRequired) => {
                        if (refreshRequired) {
                            this.navigationService.reloadAll();
                        }

                        this.navigationService.redirectToFoundation();
                    });
                });
            }
            else if (currentUser.AccountUserMappings.length == 1) {
                this.preferencesService.reconcileLanguagesRequiresRefresh().then((refreshRequired) => {
                    if (refreshRequired) {
                        this.navigationService.reloadAll();
                    }

                    this.loginService.accountRedirect();
                });
            }
            else if (currentUser.AccountUserMappings.length > 1) {
                this.showAccountPickerScreen();
                this.loggingIn = false;
            }
        });
    }

    private resetStates(): void {
        this.sentEmail = false;
        this.sendingEmail = false;

        this.inError = false;
        this.errorMessage = "";
    }

    private checkAutomaticLogin(): void {
        const loginCode = this.$location.search()["code"];
        if (loginCode) {
            // Prevents users clicking login too soon
            this.loggingIn = true;

            this.$timeout(() => {
                // Wait a before doing anything to prevent a race condition
                this.loginViaCode(loginCode);
            }, 500);
        }

        const loginToken = this.$location.search()["token"];
        if (loginToken) {
            // Prevents users clicking login too soon
            this.loggingIn = true;
            const loginResponse = JSON.parse(atob(loginToken)) as ILoginResponse;

            this.$timeout(() => {
                // Wait a before doing anything to prevent a race condition
                this.loginViaToken(true, loginResponse);
            }, 0);
        }
    }
}

export class LoginBoxComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {};

        this.controller = ComponentController;
        this.template = require("./loginBox.component.html");
    }
}
